/**
 * Controles de formulário e de filtro.
 *
 * Sem UI kit, por regra do projeto. São elementos nativos com tokens — o que
 * também resolve teclado, leitor de tela e autofill de graça.
 */

import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react'

import estilos from './Campo.module.css'

export function Campo({
  rotulo,
  dica,
  children,
  htmlFor,
}: {
  rotulo: string
  dica?: ReactNode
  children: ReactNode
  htmlFor?: string
}) {
  return (
    <div className={estilos.campo}>
      <label className={estilos.rotulo} htmlFor={htmlFor}>
        {rotulo}
      </label>
      {children}
      {dica && <span className={estilos.dica}>{dica}</span>}
    </div>
  )
}

type OpcaoSelecao = {
  valor: string
  rotulo: string
  /** Rótulo secundário à direita — usado para "sem dados" nas séries. */
  nota?: string
  desabilitada?: boolean
}

export function Selecao({
  rotulo,
  opcoes,
  valor,
  aoMudar,
  vazioRotulo,
  dica,
  ...resto
}: {
  rotulo: string
  opcoes: OpcaoSelecao[]
  valor: string
  aoMudar: (valor: string) => void
  /** Texto da opção "sem filtro". Omitir torna a escolha obrigatória. */
  vazioRotulo?: string
  dica?: ReactNode
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'>) {
  const id = useId()
  return (
    <Campo rotulo={rotulo} dica={dica} htmlFor={id}>
      <select
        id={id}
        className={estilos.selecao}
        value={valor}
        onChange={(evento) => aoMudar(evento.target.value)}
        {...resto}
      >
        {vazioRotulo !== undefined && <option value="">{vazioRotulo}</option>}
        {opcoes.map((opcao) => (
          <option key={opcao.valor} value={opcao.valor} disabled={opcao.desabilitada}>
            {opcao.nota ? `${opcao.rotulo} — ${opcao.nota}` : opcao.rotulo}
          </option>
        ))}
      </select>
    </Campo>
  )
}

/**
 * Seleção múltipla como lista de caixas, não `<select multiple>`.
 *
 * O `select multiple` nativo exige Ctrl+clique para escolher o segundo item, e
 * desfaz a seleção inteira num clique errado. Numa tela em que o operador
 * escolhe cinco produtos e olha o gráfico, isso é perda de trabalho a cada
 * deslize. Caixas de seleção não têm esse modo de falha.
 *
 * Nenhuma marcada significa "todos" — e o rótulo diz isso, em vez de o gráfico
 * abrir vazio sem explicação.
 */
export function SelecaoMultipla({
  rotulo,
  opcoes,
  valores,
  aoMudar,
  vazioSignifica = 'todos',
  altura = 168,
}: {
  rotulo: string
  opcoes: OpcaoSelecao[]
  valores: string[]
  aoMudar: (valores: string[]) => void
  vazioSignifica?: string
  altura?: number
}) {
  const marcados = new Set(valores)

  function alternar(valor: string) {
    const proximo = new Set(marcados)
    if (proximo.has(valor)) proximo.delete(valor)
    else proximo.add(valor)
    aoMudar([...proximo])
  }

  return (
    <fieldset className={estilos.grupo}>
      <legend className={estilos.rotulo}>
        {rotulo}
        <span className={estilos.contador}>
          {valores.length === 0 ? vazioSignifica : `${valores.length} de ${opcoes.length}`}
        </span>
      </legend>
      <div className={estilos.lista} style={{ maxHeight: altura }}>
        {opcoes.length === 0 && <span className={estilos.listaVazia}>Nada cadastrado.</span>}
        {opcoes.map((opcao) => (
          <label key={opcao.valor} className={estilos.opcao}>
            <input
              type="checkbox"
              checked={marcados.has(opcao.valor)}
              onChange={() => alternar(opcao.valor)}
            />
            <span className={estilos.opcaoRotulo}>{opcao.rotulo}</span>
            {opcao.nota && <span className={estilos.opcaoNota}>{opcao.nota}</span>}
          </label>
        ))}
      </div>
      {valores.length > 0 && (
        <button type="button" className={estilos.limpar} onClick={() => aoMudar([])}>
          Limpar seleção
        </button>
      )}
    </fieldset>
  )
}

export function Entrada({
  rotulo,
  dica,
  ...resto
}: { rotulo: string; dica?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId()
  return (
    <Campo rotulo={rotulo} dica={dica} htmlFor={id}>
      <input id={id} className={estilos.entrada} {...resto} />
    </Campo>
  )
}

/**
 * Interruptor booleano — uma flag ligada/desligada.
 *
 * Um `<input type="checkbox">` de verdade (teclado e leitor de tela de graça),
 * com o rótulo ao lado em vez de acima, porque a resposta é sim/não e não um
 * valor a digitar. Usado para `pode_ingerir`/`is_active` no painel de usuários.
 */
export function Interruptor({
  rotulo,
  descricao,
  checked,
  aoMudar,
  ...resto
}: {
  rotulo: string
  descricao?: ReactNode
  checked: boolean
  aoMudar: (marcado: boolean) => void
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'checked' | 'onChange' | 'type'>) {
  const id = useId()
  return (
    <div className={estilos.campo}>
      <label className={estilos.interruptor} htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(evento) => aoMudar(evento.target.checked)}
          {...resto}
        />
        <span className={estilos.interruptorTexto}>{rotulo}</span>
      </label>
      {descricao && <span className={estilos.dica}>{descricao}</span>}
    </div>
  )
}
